import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Divider,
	Paper,
	TextField,
	Typography,
} from "@mui/material";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface RequestInfo {
	requesterName: string;
	supplierName: string;
	year: number;
	message: string | null;
	signupPath: string;
	respondedTonnes: number | null;
}

/**
 * PUBLIC supplier landing (P4 item 23): a supplier opens the link from a
 * data request email. They can answer directly with a figure — no account
 * required — or optionally sign up to track emissions properly (the signup
 * carries the request token for attribution). Never force the paid path.
 */
const SupplierRequestLanding: React.FC = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token") || "";
	const [info, setInfo] = useState<RequestInfo | null>(null);
	const [state, setState] = useState<"loading" | "ok" | "notfound">(
		"loading",
	);
	const [tonnes, setTonnes] = useState("");
	const [name, setName] = useState("");
	const [notes, setNotes] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const submitFigure = async () => {
		setSubmitting(true);
		setError(null);
		try {
			const res = await fetch(`${API}/api/supplier-request/respond`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token, tonnes, name, notes }),
			});
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(
					json.Message || "Could not submit — please try again",
				);
			setSubmitted(Number(tonnes));
		} catch (e: any) {
			setError(e.message || "Could not submit — please try again");
		} finally {
			setSubmitting(false);
		}
	};

	useEffect(() => {
		if (!token) {
			setState("notfound");
			return;
		}
		fetch(
			`${API}/api/supplier-request/public?token=${encodeURIComponent(token)}`,
		)
			.then(async (res) => {
				if (!res.ok) throw new Error("notfound");
				const data = await res.json();
				setInfo(data.Data || data);
				setState("ok");
			})
			.catch(() => setState("notfound"));
	}, [token]);

	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="70vh"
			padding={2}
		>
			<Paper
				elevation={0}
				sx={{
					maxWidth: 520,
					padding: "40px 32px",
					textAlign: "center",
					border: "1px solid rgba(0, 23, 28, 0.10)",
					borderRadius: "12px",
				}}
			>
				{state === "loading" && <CircularProgress />}
				{state === "notfound" && (
					<>
						<Typography variant="h6" gutterBottom>
							This request link is not valid
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Check the link in your email, or contact the company
							that sent it to you.
						</Typography>
					</>
				)}
				{state === "ok" && info && (
					<>
						<Typography variant="h5" gutterBottom>
							{info.requesterName} has requested your {info.year}{" "}
							emissions data
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ my: 2 }}
						>
							Hi {info.supplierName} — {info.requesterName} needs
							your greenhouse gas emissions for their supply-chain
							reporting. Enter your figure below, or connect an
							ARTEH account if you track it there.
						</Typography>
						{info.message && (
							<Typography
								variant="body2"
								sx={{
									borderLeft: "3px solid #7cced1",
									pl: 2,
									my: 2,
									textAlign: "left",
									color: "text.secondary",
								}}
							>
								{info.message}
							</Typography>
						)}

						{submitted !== null ? (
							<Alert severity="success" sx={{ my: 2, textAlign: "left" }}>
								Thanks — {submitted.toLocaleString()} t CO₂e has
								been sent to {info.requesterName}. You can
								re-open this link any time to update it.
							</Alert>
						) : (
							<Box sx={{ textAlign: "left", mt: 2 }}>
								<Typography variant="subtitle2" gutterBottom>
									Enter your {info.year} emissions
								</Typography>
								{info.respondedTonnes != null && (
									<Alert severity="info" sx={{ mb: 2 }}>
										You previously submitted{" "}
										{Number(info.respondedTonnes).toLocaleString()}{" "}
										t CO₂e — submitting again updates it.
									</Alert>
								)}
								{error && (
									<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
										{error}
									</Alert>
								)}
								<TextField
									fullWidth
									size="small"
									label="Total emissions (t CO₂e)"
									type="number"
									value={tonnes}
									onChange={(e) => setTonnes(e.target.value)}
									helperText="Your total greenhouse gas emissions for the year, in tonnes of CO₂ equivalent. An estimate is fine — say so in the note."
									sx={{ mb: 2 }}
								/>
								<TextField
									fullWidth
									size="small"
									label="Your name (optional)"
									value={name}
									onChange={(e) => setName(e.target.value)}
									sx={{ mb: 2 }}
								/>
								<TextField
									fullWidth
									size="small"
									label="Note (optional — e.g. how it was calculated)"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									multiline
									minRows={2}
									sx={{ mb: 2 }}
								/>
								<Button
									fullWidth
									variant="contained"
									color="secondary"
									disabled={submitting || tonnes.trim() === ""}
									onClick={submitFigure}
								>
									{submitting ? "Sending…" : `Send to ${info.requesterName}`}
								</Button>
							</Box>
						)}

						<Divider sx={{ my: 3 }}>or</Divider>
						<Typography variant="body2" color="text.secondary">
							Want to measure it properly? ARTEH Premium tracks
							your emissions and shares a ready-made data pack in
							a few clicks.
						</Typography>
						<Button
							component={Link}
							to={info.signupPath}
							variant="outlined"
							color="secondary"
							sx={{ mt: 1.5 }}
						>
							Try ARTEH Premium
						</Button>
						<Typography
							variant="caption"
							display="block"
							color="text.secondary"
							sx={{ mt: 2 }}
						>
							Already using ARTEH? Log in and send{" "}
							{info.requesterName} a data pack from the Customer
							data pack page.
						</Typography>
					</>
				)}
			</Paper>
		</Box>
	);
};

export default SupplierRequestLanding;
